// U-2 `/upload/processing/:id` — экран обработки с polling backoff 2→5→10s + 5min timeout.
// Source: docs/specs/sprint-4/stage-4-frontend.md §4.2 + §2.3.2.
//
// Переходы по status:
//   processing            → ничего, ждём (после 5 мин — inline timeout banner)
//   awaiting_user_review  → router.push('/') (HomePending через OnboardingRouter, Q1)
//   failed                → toast по failure_reason + router.push('/upload')
//   success/reported      → defensively на /upload/history (штатно не приходит сюда)

import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useUploadPolling, POLL_TIMEOUT_MS } from '@/api/hooks/uploads';
import { queryKeys } from '@/api/queryKeys';
import { setReviewOrigin } from '@/lib/uploadNav';
import { usePollingFailureToast } from '@/api/error-toast';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { PushPermissionPrompt } from '@/components/push/PushPermissionPrompt';
import { WaitingHint } from '@/components/uploads/WaitingHint';
import { ScreenHeader } from '@/components/layout/ScreenHeader';

export function Processing() {
  const params = useParams<{ id: string }>();
  const uploadId = Number(params.id);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const [showTimeoutBanner, setShowTimeoutBanner] = useState(false);
  const showPollingFailure = usePollingFailureToast();
  const { data } = useUploadPolling(uploadId);
  // useUploadPolling теперь возвращает GetUploadResponse — Upload лежит на .upload.
  const upload = data?.upload;

  useEffect(() => {
    const t = setTimeout(() => setShowTimeoutBanner(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!upload || upload.status === 'processing') return;
    let cancelled = false;
    (async () => {
      // useUploadPolling обновляет single-upload cache, но не list-by-status.
      // Без await invalidate OnboardingRouter читает stale pendingQuery → derives
      // surveyDone → <HomeEmpty /> inline на / (Sprint 5A.1, H1 fix).
      await qc.invalidateQueries({ queryKey: queryKeys.uploads });
      if (cancelled) return;
      switch (upload.status) {
        case 'awaiting_user_review':
          // Откуда пришли, объявляем ЯВНО (круг ревью, находка 2). Признак
          // глобальный и живёт до перезаписи: без этого решение принимал бы
          // устаревший признак от прошлого потока — человек, раньше разбиравший
          // из «Моих загрузок», после новой загрузки с Главной уехал бы в
          // историю, а поздравление с первым анализом осталось бы непоказанным.
          setReviewOrigin('home');

          // HEAL-46a: ведём на проверку ИМЕННО ЭТОГО анализа, а не на «/».
          //
          // Раньше здесь был переход на Главную, потому что она подменялась
          // экраном проверки. Подмену убрали (D-18) — при пакете она запирала
          // человека, — и переход на «/» стал вести не туда: человек ждал
          // результат своей загрузки, а получал Главную.
          setLocation(`/uploads/${upload.id}/review`);
          return;
        case 'failed':
          if (upload.failure_reason) showPollingFailure(upload.failure_reason);
          setLocation('/upload');
          return;
        case 'success':
        case 'reported':
          setLocation('/upload/history');
          return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [upload?.status, upload?.failure_reason, setLocation, showPollingFailure, qc, upload]);

  if (Number.isNaN(uploadId)) {
    return (
      <MobileFrame>
        <ScreenHeader onBack={() => setLocation('/')} title="Загрузка анализа" />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-muted-foreground">Неверная ссылка.</p>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/')} title="Загрузка анализа" />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary-text" aria-hidden />
        <h1 className="text-xl font-semibold">Распознаём анализ…</h1>
        <p className="text-sm text-muted-foreground">Обычно занимает 15–30 секунд</p>
        {/* HEAL-46b D-30/D-31: одиночная загрузка уведомляет наравне с пакетом,
            и спросить надо там же — когда файл принят и пошёл в обработку.
            Экран уводит сам, как только анализ готов; если человек не успел
            ответить, вопрос просто исчезает — засчитанным отказ не будет. */}
        <div className="w-full max-w-sm text-left">
          <PushPermissionPrompt active={!upload || upload.status === 'processing'} />
        </div>
        <WaitingHint className="text-sm text-muted-foreground" />
        {showTimeoutBanner && (
          <div className="mt-4 rounded-control border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {/* HEAL-46b: «не закрывайте приложение» убрано. Совет был неверен
                и до push — обработка идёт на сервере и от закрытия не
                зависит, — а с ним стал прямо противоположен тому, что мы
                обещаем строкой выше. Держать человека у экрана лишние минуты,
                когда держать незачем, — худшее, что тут можно сделать. */}
            <strong>Обработка занимает чуть больше времени.</strong> Большой бланк
            распознаётся дольше — можно заняться своими делами.
          </div>
        )}
      </div>
    </MobileFrame>
  );
}
